import React, { Suspense, useState, useEffect } from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { useParams, useHistory } from 'react-router-dom';
import { useRecoilState, useRecoilValue } from 'recoil';
import ReactPlayer from 'react-player';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faThumbsUp } from '@fortawesome/pro-duotone-svg-icons';
import { notificationsAtom, userAtom } from '../../../atoms/Atoms';
import MainPage from '../../shared-components/main-page/MainPage';
import { H1 } from '../../shared-components/typography/Title';
import api from '../../../api/api';
import Loader from '../../shared-components/loader/Loader';
import { Spinner } from '../../shared-components/Spinner';
import { getLikesAbbrNumber } from '../../../functions';

function LearningVideo() {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const { videoId } = useParams();
  const profile = useRecoilValue(userAtom);
  const [notifications, setNotifications] = useRecoilState(notificationsAtom);
  const [loading, setLoading] = useState(false);
  const [loadingLike, setLoadingLike] = useState(false);
  const [video, setVideo] = useState(null);

  function likeVideo() {
    setLoadingLike(true);
    api.post(`learning/like/video/?learningVideoId=${videoId}`, null)
      .then((response) => {
        if (response.data.success) {
          setVideo({
            ...video,
            likes: response.data.data,
            likedByUser: true
          });
        } else {
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response.data.error,
              error: true,
            },
          ]);
        }
      })
      .catch((error) => {
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          },
        ]);
      })
      .finally(() => {
        setLoadingLike(false);
      });
  }

  function unlikeVideo() {
    setLoadingLike(true);
    api.post(`learning/unlike/video?learningVideoId=${videoId}`, null)
      .then((response) => {
        if (response.data.success) {
          setVideo({
            ...video,
            likes: response.data.data,
            likedByUser: false
          });
        } else {
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response.data.error,
              error: true,
            },
          ]);
        }
      })
      .catch((error) => {
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          },
        ]);
      })
      .finally(() => {
        setLoadingLike(false);
      });
  }

  function getVideo() {
    if (!videoId) {
      return;
    }
    setLoading(true);
    api.get(`learning/video/${videoId}`)
      .then((response) => {
        if (response.data.success) {
          setVideo(response.data.data);
        } else {
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response.data.error,
              error: true,
            },
          ]);
        }
      })
      .catch((error) => {
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          },
        ]);
        history.push('/learning');
      })
      .finally(() => {
        setLoading(false);
      });
  }

  useEffect(() => {
    getVideo();
  }, [videoId]);

  return (
    <MainPage>
      <div className="flex mt-4 space-y-2 space-x-2">
        <button
          type="button"
          className="flex cursor-pointer space-x-2 mt-4"
          onClick={() => history.push('/learning')}
        >
          <FontAwesomeIcon className="text-xl" icon={faArrowLeft} />
        </button>
        {video && <H1 text={i18n.language === 'en' ? `${video?.category.department.nameEng} / ${video?.category.nameEng} / ${video?.nameEng}` : `${video?.category.department.nameSpa} / ${video?.category.nameSpa} / ${video?.nameSpa}`} className="mr-auto" />}
      </div>
      {loading && (
        <div className="flex">
          <Spinner className="mx-auto mt-44 text-accentColor" big />
        </div>
      )}
      {!loading && (
        <div>
          <div className="grid gap-4 grid-cols-1 lg:grid-cols-5 xl:grid-cols-4">
            <div className="col-span-1 hidden lg:block" />
            <div className="col-span-1 lg:col-span-3 xl:col-span-2">
              <Suspense
                fallback={(
                  <></>
                )}
              >
                <ReactPlayer preload style={{ width: '100%', height: 'auto', aspectRatio: '16/9' }} controls={true} volume={0.25} src={i18n.language === 'en' ? video?.urlEng : video?.urlSpa} />
              </Suspense>
              {profile && (
                <div className="flex mt-1">
                  <button
                    type="button"
                    disabled={loadingLike}
                    className="text-xl flex h-8 ml-auto bg-primaryColor px-3 rounded-full"
                    onClick={() => {
                      if (video?.likedByUser) {
                        unlikeVideo();
                      } else {
                        likeVideo();
                      }
                    }}
                  >
                    {loadingLike && (
                      <Loader className="p-2 my-auto" color="bg-white" />
                    )}
                    {!loadingLike && (
                      <span className="space-x-2 my-auto">
                        <FontAwesomeIcon className={video?.likedByUser ? 'text-white hover:text-accentColor' : 'text-accentColor hover:text-white'} icon={faThumbsUp} />
                        <span className="text-white">{getLikesAbbrNumber(video?.likes)}</span>
                      </span>
                    )}
                  </button>
                </div>
              )}
            </div>
            <div className="col-span-1 hidden lg:block" />
            <div className="col-span-1 mt-6">
              {i18n.language === 'en' ? video?.descriptionEng : video?.descriptionSpa}
            </div>
          </div>
        </div>
      )}
    </MainPage>
  );
}

export default LearningVideo;
